import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CustomerHeader } from "@/components/CustomerHeader";
import { CustomerFooter } from "@/components/CustomerFooter";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Gift, ShoppingBag, CreditCard, ArrowLeft, Search, Filter, Calendar, Crown, Loader2, Sparkles, ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, differenceInDays, addYears, isBefore } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";

const SESSION_KEY = "shakatah_customer_session";

interface GiftEvent {
  id: string;
  event_name: string;
  recipient_name: string | null;
  event_date: string;
  occasion: string | null;
  is_recurring: boolean;
  status: string;
  role: 'organizer' | 'participant';
}

interface GroupActivity {
  id: string;
  title: string;
  organizer_name: string | null;
  target_date: string | null;
  status: string;
  payment_type: string | null;
  collected_amount: number | null;
  target_amount: number | null;
  created_at: string;
  role: 'organizer' | 'admin' | 'participant';
}

const OCCASION_EMOJIS: Record<string, string> = {
  birthday: "🎂",
  anniversary: "💍",
  wedding: "👰",
  baby_shower: "👶",
  graduation: "🎓",
  housewarming: "🏠",
  retirement: "🎉",
  promotion: "🚀",
  festival: "🪔",
  other: "✨",
};

const MyActivity = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  const [giftEvents, setGiftEvents] = useState<GiftEvent[]>([]);
  const [groupPurchases, setGroupPurchases] = useState<GroupActivity[]>([]);
  const [groupPayments, setGroupPayments] = useState<GroupActivity[]>([]);
  
  // Expanded states (show more than 5)
  const [giftsExpanded, setGiftsExpanded] = useState(false);
  const [purchasesExpanded, setPurchasesExpanded] = useState(false);
  const [paymentsExpanded, setPaymentsExpanded] = useState(false);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = () => {
    const storedSession = sessionStorage.getItem(SESSION_KEY);
    if (storedSession) {
      try {
        const session = JSON.parse(storedSession);
        if (session.email) {
          setUserEmail(session.email);
          fetchAllActivity(session.email);
          return;
        }
      } catch {
        // Invalid session
      }
    }
    navigate("/my-purchases");
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    navigate("/my-purchases");
  };

  const fetchAllActivity = async (email: string) => {
    setLoading(true);
    try {
      const emailLower = email.toLowerCase();
      
      const { data: organizerEvents } = await supabase
        .from("gift_reminders")
        .select("id, event_name, recipient_name, event_date, occasion, is_recurring")
        .eq("customer_email", emailLower)
        .order("event_date", { ascending: false });
      
      const { data: invitedEvents } = await supabase
        .from("gift_reminders")
        .select("id, event_name, recipient_name, event_date, occasion, is_recurring")
        .or(`reminder_email.eq.${emailLower},invited_friend_email.eq.${emailLower}`)
        .order("event_date", { ascending: false });
      
      const allGiftEvents: GiftEvent[] = [];
      organizerEvents?.forEach(e => {
        if (!allGiftEvents.find(x => x.id === e.id)) {
          allGiftEvents.push({ ...e, status: 'active', role: 'organizer' });
        }
      });
      invitedEvents?.forEach(e => {
        if (!allGiftEvents.find(x => x.id === e.id)) {
          allGiftEvents.push({ ...e, status: 'active', role: 'participant' });
        }
      });
      setGiftEvents(allGiftEvents);
      
      const { data: organizerActivities } = await supabase
        .from("group_purchases")
        .select("id, title, organizer_name, target_date, status, payment_type, collected_amount, target_amount, created_at, category")
        .eq("organizer_email", emailLower)
        .order("created_at", { ascending: false });
      
      const { data: participantData } = await supabase
        .from("group_purchase_participants")
        .select("purchase_id, role")
        .eq("email", emailLower)
        .eq("status", "active");
      
      let participantActivities: any[] = [];
      if (participantData && participantData.length > 0) {
        const purchaseIds = participantData.map(p => p.purchase_id);
        const { data: activities } = await supabase
          .from("group_purchases")
          .select("id, title, organizer_name, target_date, status, payment_type, collected_amount, target_amount, created_at, category")
          .in("id", purchaseIds);
        participantActivities = activities || [];
      }
      
      const allActivities: GroupActivity[] = [];
      const roleMap = new Map(participantData?.map(p => [p.purchase_id, p.role]) || []);
      
      organizerActivities?.forEach(a => {
        if (!allActivities.find(x => x.id === a.id)) {
          allActivities.push({ ...a, role: 'organizer' });
        }
      });
      participantActivities.forEach(a => {
        if (!allActivities.find(x => x.id === a.id)) {
          const role = roleMap.get(a.id) === 'admin' ? 'admin' : 'participant';
          allActivities.push({ ...a, role });
        }
      });
      
      const purchases = allActivities.filter(a => (a as any).category === 'purchase' || !(a as any).category);
      const payments = allActivities.filter(a => (a as any).category === 'payment');
      
      setGroupPurchases(purchases);
      setGroupPayments(payments);
    } catch (error) {
      console.error("Error fetching activity:", error);
    } finally {
      setLoading(false);
    }
  };

  const getNextOccurrence = (eventDate: string, isRecurring: boolean) => {
    const date = new Date(eventDate);
    if (!isRecurring) return date;
    
    const today = new Date();
    let nextDate = new Date(today.getFullYear(), date.getMonth(), date.getDate());
    if (isBefore(nextDate, today)) {
      nextDate = addYears(nextDate, 1);
    }
    return nextDate;
  };

  const getDaysUntil = (eventDate: string, isRecurring: boolean = false) => {
    const nextDate = getNextOccurrence(eventDate, isRecurring);
    return differenceInDays(nextDate, new Date());
  };

  const getStatusBadge = (status: string, daysUntil?: number) => {
    if (status === 'closed' || status === 'completed') {
      return <Badge variant="secondary" className="text-[10px] px-1.5 h-5">Done</Badge>;
    }
    if (status === 'cancelled') {
      return <Badge variant="destructive" className="text-[10px] px-1.5 h-5">Cancelled</Badge>;
    }
    if (daysUntil !== undefined) {
      if (daysUntil <= 0) return <Badge className="bg-rose-500 text-white text-[10px] px-1.5 h-5 border-0">Today!</Badge>;
      if (daysUntil <= 7) return <Badge className="bg-amber-500 text-white text-[10px] px-1.5 h-5 border-0">{daysUntil}d</Badge>;
    }
    return <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-[10px] px-1.5 h-5 border-0">Active</Badge>;
  };

  const filterItems = <T extends { status?: string }>(items: T[], searchFn: (item: T) => boolean) => {
    return items.filter(item => {
      const matchesSearch = searchFn(item);
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && item.status !== 'closed' && item.status !== 'cancelled') ||
        (statusFilter === 'completed' && (item.status === 'closed' || item.status === 'completed')) ||
        (statusFilter === 'cancelled' && item.status === 'cancelled');
      return matchesSearch && matchesStatus;
    });
  };

  const filteredGifts = filterItems(giftEvents, (g) => 
    g.event_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.recipient_name?.toLowerCase().includes(searchQuery.toLowerCase()) || false
  );

  const filteredPurchases = filterItems(groupPurchases, (p) => 
    p.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPayments = filterItems(groupPayments, (p) => 
    p.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalCount = filteredGifts.length + filteredPurchases.length + filteredPayments.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50/60 via-background to-violet-50/60 dark:from-rose-950/20 dark:via-background dark:to-violet-950/20 flex flex-col">
      <SEO 
        title="My Activity | Shakatah"
        description="View all your group gifts, purchases, and payments activity"
      />
      
      <CustomerHeader userEmail={userEmail || undefined} onLogout={handleLogout} />
      
      <main className="flex-1 container pt-16 pb-8 max-w-6xl mx-auto px-4">
        {/* Header */}
        <Link 
          to="/my-purchases" 
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-5"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 via-violet-500 to-emerald-500 flex items-center justify-center shadow-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">My Activity</h1>
              <p className="text-sm text-muted-foreground">{totalCount} items across all categories</p>
            </div>
          </div>
        </motion.div>
        
        {/* Compact Search & Filter */}
        <div className="flex gap-2 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Search..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 bg-background/80"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[120px] h-9 bg-background/80">
              <Filter className="w-3.5 h-3.5 mr-1.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Three Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Gifts Column */}
          <CategoryColumn
            title="Group Gifts"
            icon={<Gift className="w-4 h-4" />}
            count={filteredGifts.length}
            isExpanded={giftsExpanded}
            onExpandToggle={() => setGiftsExpanded(!giftsExpanded)}
            colorConfig={{
              bg: "bg-gradient-to-r from-rose-500 to-pink-500",
              lightBg: "bg-rose-50 dark:bg-rose-950/30",
              border: "border-rose-200 dark:border-rose-800/50",
              text: "text-rose-600 dark:text-rose-400",
            }}
            emptyMessage="No gifts found"
            statusFilter={statusFilter}
          >
            {(giftsExpanded ? filteredGifts : filteredGifts.slice(0, 5)).map((event) => (
              <GiftRow key={event.id} event={event} getDaysUntil={getDaysUntil} getStatusBadge={getStatusBadge} />
            ))}
          </CategoryColumn>
          
          {/* Purchases Column */}
          <CategoryColumn
            title="Group Purchases"
            icon={<ShoppingBag className="w-4 h-4" />}
            count={filteredPurchases.length}
            isExpanded={purchasesExpanded}
            onExpandToggle={() => setPurchasesExpanded(!purchasesExpanded)}
            colorConfig={{
              bg: "bg-gradient-to-r from-violet-500 to-purple-500",
              lightBg: "bg-violet-50 dark:bg-violet-950/30",
              border: "border-violet-200 dark:border-violet-800/50",
              text: "text-violet-600 dark:text-violet-400",
            }}
            emptyMessage="No purchases found"
            statusFilter={statusFilter}
          >
            {(purchasesExpanded ? filteredPurchases : filteredPurchases.slice(0, 5)).map((activity) => (
              <ActivityRow key={activity.id} activity={activity} type="purchase" getStatusBadge={getStatusBadge} />
            ))}
          </CategoryColumn>
          
          {/* Payments Column */}
          <CategoryColumn
            title="Group Payments"
            icon={<CreditCard className="w-4 h-4" />}
            count={filteredPayments.length}
            isExpanded={paymentsExpanded}
            onExpandToggle={() => setPaymentsExpanded(!paymentsExpanded)}
            colorConfig={{
              bg: "bg-gradient-to-r from-emerald-500 to-teal-500",
              lightBg: "bg-emerald-50 dark:bg-emerald-950/30",
              border: "border-emerald-200 dark:border-emerald-800/50",
              text: "text-emerald-600 dark:text-emerald-400",
            }}
            emptyMessage="No payments found"
            statusFilter={statusFilter}
          >
            {(paymentsExpanded ? filteredPayments : filteredPayments.slice(0, 5)).map((activity) => (
              <ActivityRow key={activity.id} activity={activity} type="payment" getStatusBadge={getStatusBadge} />
            ))}
          </CategoryColumn>
        </div>
        
        {/* Empty State */}
        {totalCount === 0 && (
          <Card className="p-8 text-center mt-6 bg-gradient-to-br from-muted/30 to-muted/50">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-500/20 via-violet-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-3">
              <Calendar className="w-7 h-7 text-muted-foreground" />
            </div>
            <h3 className="font-semibold mb-1">No activities found</h3>
            <p className="text-sm text-muted-foreground mb-4">Start by creating a group gift, purchase, or payment!</p>
            <Button onClick={() => navigate("/my-purchases")} size="sm" className="bg-gradient-to-r from-rose-500 via-violet-500 to-emerald-500 text-white border-0">
              Go to Dashboard
            </Button>
          </Card>
        )}
      </main>
      
      <footer className="border-t border-border/50 bg-card/95">
        <div className="container py-2">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Gift className="w-3.5 h-3.5 text-customer" />
              <span className="font-semibold text-foreground text-xs">Shakatah</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/refund-policy" className="hover:text-foreground transition-colors">Refunds</Link>
              <a href="/#faq" className="hover:text-foreground transition-colors">FAQ</a>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Category Column Component
interface ColorConfig {
  bg: string;
  lightBg: string;
  border: string;
  text: string;
}

const CategoryColumn = ({ 
  title, 
  icon, 
  count, 
  isExpanded,
  onExpandToggle,
  colorConfig, 
  emptyMessage,
  statusFilter,
  children 
}: { 
  title: string;
  icon: React.ReactNode;
  count: number;
  isExpanded: boolean;
  onExpandToggle: () => void;
  colorConfig: ColorConfig;
  emptyMessage: string;
  statusFilter: string;
  children: React.ReactNode;
}) => {
  const showEmptyCancelledMessage = count === 0 && statusFilter === 'cancelled';
  
  return (
    <Card className={`overflow-hidden border ${colorConfig.border} transition-all h-fit`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-3 ${colorConfig.lightBg}`}>
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg ${colorConfig.bg} flex items-center justify-center text-white shadow-sm`}>
            {icon}
          </div>
          <span className={`font-semibold text-sm ${colorConfig.text}`}>{title}</span>
        </div>
        <Badge variant="secondary" className="h-5 px-2 text-[10px] font-medium">{count}</Badge>
      </div>
      
      {/* Content */}
      <CardContent className="p-2">
        {showEmptyCancelledMessage ? (
          <div className="text-center py-4">
            <span className="text-lg">🎉</span>
            <p className="text-sm text-muted-foreground mt-1">Yay! No cancelled events</p>
          </div>
        ) : count === 0 ? (
          <p className="text-sm text-muted-foreground py-3 text-center">{emptyMessage}</p>
        ) : (
          <div className="space-y-1.5">
            {children}
            
            {/* Expand/Collapse button */}
            {count > 5 && (
              <button 
                onClick={onExpandToggle}
                className={`w-full text-xs ${colorConfig.text} hover:opacity-80 text-center py-2 flex items-center justify-center gap-1 transition-colors`}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-3 w-3" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3" />
                    +{count - 5} more
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Gift Row Component
const GiftRow = ({ event, getDaysUntil, getStatusBadge }: { 
  event: GiftEvent; 
  getDaysUntil: (date: string, recurring: boolean) => number;
  getStatusBadge: (status: string, days?: number) => React.ReactNode;
}) => {
  const daysUntil = getDaysUntil(event.event_date, event.is_recurring);
  
  return (
    <Link to={`/events/${event.id}`} className="block">
      <div className="flex items-center justify-between p-2.5 rounded-lg bg-background hover:bg-rose-50/50 dark:hover:bg-rose-950/20 transition-colors group">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className="text-lg flex-shrink-0">{event.occasion ? OCCASION_EMOJIS[event.occasion] || "🎁" : "🎁"}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium text-sm text-foreground truncate">{event.event_name}</p>
              {event.role === 'organizer' && <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              <span>{format(new Date(event.event_date), "MMM d, yyyy")}</span>
              {event.is_recurring && <Badge variant="outline" className="text-[9px] px-1 h-4">Yearly</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {getStatusBadge(event.status, daysUntil)}
          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </div>
      </div>
    </Link>
  );
};

// Activity Row Component
const ActivityRow = ({ activity, type, getStatusBadge }: { 
  activity: GroupActivity; 
  type: 'purchase' | 'payment';
  getStatusBadge: (status: string, days?: number) => React.ReactNode;
}) => {
  const isPurchase = type === 'purchase';
  const hoverClass = isPurchase ? "hover:bg-violet-50/50 dark:hover:bg-violet-950/20" : "hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20";
  
  return (
    <Link to={`/purchases/${activity.id}`} className="block">
      <div className={`flex items-center justify-between p-2.5 rounded-lg bg-background ${hoverClass} transition-colors group`}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isPurchase ? "bg-violet-100 dark:bg-violet-900/40" : "bg-emerald-100 dark:bg-emerald-900/40"
          }`}>
            {isPurchase ? (
              <ShoppingBag className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            ) : (
              <CreditCard className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium text-sm text-foreground truncate">{activity.title}</p>
              {activity.role === 'organizer' && <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              <span>{activity.target_date ? format(new Date(activity.target_date), "MMM d, yyyy") : format(new Date(activity.created_at), "MMM d, yyyy")}</span>
              {type === 'payment' && activity.target_amount && (
                <span className={`font-medium ${isPurchase ? "text-violet-600 dark:text-violet-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                  ₹{(activity.collected_amount || 0).toLocaleString()} / ₹{activity.target_amount.toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {getStatusBadge(activity.status)}
          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </div>
      </div>
    </Link>
  );
};

export default MyActivity;
